import clsx from 'clsx';
import Skeleton from 'react-loading-skeleton';

import ExpendUpArrowIcon from '~/shared/assets/icons/expend-up-arrow.svg';
import Chevron from '~/shared/assets/icons/chevron.svg';
import InfoCircleIcon from '~/shared/assets/icons/info-circle.svg';
import Typography from '~/shared/components/Typography';
import Tag from '~/shared/components/Tag';
import Button from '~/shared/components/ButtonsV2';
// import Skeleton from '~/shared/components/Skeleton';

import type { ItemInstance } from '@headless-tree/core';
import type { ConfigTreeNode } from '~/settings/components/ConfigurationsPage/types';
import { formatConfigurationLevelLabel } from '~/settings/components/ConfigurationsPage/helpers/utils';

interface HeadlessTreeNodeProps {
  item: ItemInstance<ConfigTreeNode>;
  selectedNodeId: string | null;
  onSelect: (nodeId: string) => void;
  hasChevron?: boolean;
  count?: number;
  showPlus?: boolean;
  showSelectButton?: boolean;
  hideHoverEffect?: boolean;
}

export default function HeadlessTreeNode({
  item,
  selectedNodeId,
  onSelect,
  hasChevron,
  count,
  showPlus,
  showSelectButton,
  hideHoverEffect,
}: HeadlessTreeNodeProps) {
  const nodeData = item.getItemData();
  const meta = item.getItemMeta();
  const level = meta.level;
  const isExpanded = item.isExpanded();
  const isSelected = item.isSelected() || selectedNodeId === item.getId();
  const hasChildren = hasChevron ?? item.isFolder();
  const paddingLeft = level * 20 + 13;

  const displayName =
    nodeData.name.length > 26 ? `${nodeData.name.substring(0, 26)}...` : nodeData.name;

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasChildren) {
      if (isExpanded) {
        item.collapse();
      } else {
        item.expand();
      }
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    const nodeId = item.getId();
    item.setFocused();
    item.getTree().setSelectedItems([nodeId]);
    onSelect(nodeId);
  };

  const itemProps = item.getProps();
  const mergedProps = {
    ...itemProps,
    onClick: handleClick,
  };

  return (
    <div className="flex flex-col relative">
      <div className="flex relative w-full">
        <div
          {...mergedProps}
          className={clsx(
            'h-[64px]',
            'gap-[8px]',
            'rounded-[8px]',
            'flex',
            'group',
            'relative',
            'transition-colors',
            'z-10',
            'flex-1',
            'p-[12px]',
            isSelected ? 'bg-bg hover:bg-bg' : '',
            hideHoverEffect ? '' : 'hover:bg-bg cursor-pointer',
            {
              'pl-[40px]': level > 0,
              // 'group-hover:bg-bg': !hideHoverEffect,
            }
          )}
          style={{ paddingLeft: `${paddingLeft}px` }}
        >
          <div className="flex items-center justify-center w-5 h-5" onClick={handleIconClick}>
            {hasChildren && (
              <ExpendUpArrowIcon
                className={clsx(
                  'w-4 h-4 group-hover:text-primary-500 hover:text-primary-500 cursor-pointer',
                  !isExpanded && 'rotate-180 transition-transform',
                  isSelected
                    ? 'text-primary-500'
                    : 'text-primary-800 group-hover:text-primary-800 hover:text-primary-800'
                )}
              />
            )}
          </div>
          <div className="flex flex-col gap-1">
            {nodeData.isLoading ? (
              <Skeleton
                containerClassName=" block leading-[unset!important]"
                className="h-[20px] 2xl:h[20px] leading-[unset!important] rounded-[8px] "
                baseColor="var(--color-gray-200)"
                highlightColor="#FFFF"
                width={80}
              />
            ) : (
              <Typography
                variant="p3"
                className={clsx('flex-1 font-family-outfit')}
                color={
                  isSelected ? 'text-primary-500' : 'text-primary-800 group-hover:text-primary-800'
                }
              >
                {displayName}
              </Typography>
            )}

            <Tag
              variant={nodeData.level}
              size="xs"
              text={formatConfigurationLevelLabel(nodeData.level)}
              className="capitalize"
            />
          </div>

          <div className="flex items-center gap-5 ml-auto my-auto">
            {showSelectButton && nodeData.hasOverrides && (
              <div className="flex gap-1">
                <InfoCircleIcon className="w-5 h-5 text-primary-300" />
                <Typography variant="p3" color="text-gray-600">
                  Overridden
                </Typography>
              </div>
            )}
            {count !== undefined && (
              <p className="text-xs text-gray-600 font-medium">
                {showPlus ? `${count}+` : count > 999 ? '999+' : count}
              </p>
            )}
            {showSelectButton && (
              <Button
                variant="third"
                size="sm"
                icon={<Chevron className="rotate-270 w-5 h-5" />}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(item.getId());
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
